/**
 * FY (Financial Year) quarter utilities shared across management fee components.
 *
 * FY quarters follow the Indian financial year:
 *   Q1 = Apr–Jun, Q2 = Jul–Sep, Q3 = Oct–Dec, Q4 = Jan–Mar (next year)
 *
 * Quarter labels use the format "Q1-2026" where the year is the FY start year.
 */

/** "2026-09-30" → "Q2-2026", the FY quarter the date falls in. */
export const fyQuarterOfDate = (isoDate: string): string => {
    const [year, month] = isoDate.split("-").map(Number);
    return month >= 4 ? `Q${Math.ceil((month - 3) / 3)}-${year}` : `Q4-${year - 1}`;
};

export const getCurrentFYQuarter = (): string => {
    const today = new Date();
    return fyQuarterOfDate(`${today.getFullYear()}-${today.getMonth() + 1}`);
};

/** The current FY quarter and the ones before it, newest first, back four FYs. */
export const getRecentFYQuarters = (): string[] => {
    const current = getCurrentFYQuarter();
    const fyStart = Number(current.split("-")[1]);
    const options: string[] = [];
    for (let fy = fyStart; fy >= fyStart - 3; fy--) {
        options.push(`Q4-${fy}`, `Q3-${fy}`, `Q2-${fy}`, `Q1-${fy}`);
    }
    return options.slice(options.indexOf(current));
};

/** "Q2-2026" → "2026-09-30", the quarter's last day. */
export const fyQuarterEndDate = (q: string): string => {
    const [qtr, year] = q.split("-");
    const fy = Number(year);
    const ends: Record<string, string> = {
        Q1: `${fy}-06-30`,
        Q2: `${fy}-09-30`,
        Q3: `${fy}-12-31`,
        Q4: `${fy + 1}-03-31`,
    };
    return ends[qtr] ?? "";
};

/**
 * Formats a quarter label like "Q1-2026" into a human-readable string.
 * e.g. "Q1-2026" → "Q1 FY27 (Apr-Jun 2026)"
 */
export const formatQuarterLabel = (q: string): string => {
    const [qtr, year] = q.split("-");
    const fy = parseInt(year);
    const monthRanges: Record<string, string> = {
        Q1: `Apr-Jun ${fy}`,
        Q2: `Jul-Sep ${fy}`,
        Q3: `Oct-Dec ${fy}`,
        Q4: `Jan-Mar ${fy + 1}`,
    };
    return `${qtr} FY${(fy + 1).toString().slice(-2)} (${monthRanges[qtr]})`;
};
